/**
 * Cache Buster Utility
 * Helps prevent chunk loading errors after deployments by detecting version mismatches
 */

const APP_VERSION_KEY = 'app_version';
const LAST_LOADED_KEY = 'last_loaded_time';

class CacheBuster {
  /**
   * Initialize cache busting
   * Call this when the app starts
   */
  static initialize() {
    const currentVersion = this.getCurrentVersion();
    const storedVersion = this.getStoredVersion();
    const lastLoaded = localStorage.getItem(LAST_LOADED_KEY);
    const now = Date.now();

    console.log('Cache Buster - Current Version:', currentVersion);
    console.log('Cache Buster - Stored Version:', storedVersion);

    // Check if version has changed since last load
    if (storedVersion && storedVersion !== currentVersion) {
      console.log('Version mismatch detected! Clearing cache...');
      this.clearCacheAndReload();
      return;
    }

    // Store current version
    this.setStoredVersion(currentVersion);
    localStorage.setItem(LAST_LOADED_KEY, now.toString());
  }

  /**
   * Get the current app version from package.json or build metadata
   * In production, you might want to inject this during build process
   */
  static getCurrentVersion() {
    // Try to get version from meta tag (set during build)
    const metaVersion = document.querySelector('meta[name="app-version"]');
    if (metaVersion) {
      return metaVersion.getAttribute('content');
    }

    // Fallback to timestamp-based versioning
    // Support both Vite (import.meta.env) and CRA (process.env)
    const version = process.env.REACT_APP_VERSION;
    return version || 'dev';
  }

  /**
   * Get the stored app version from localStorage
   */
  static getStoredVersion() {
    return localStorage.getItem(APP_VERSION_KEY);
  }

  /**
   * Store the app version in localStorage
   */
  static setStoredVersion(version) {
    localStorage.setItem(APP_VERSION_KEY, version);
  }

  /**
   * Clear all caches and reload the page
   */
  static async clearCacheAndReload() {
    try {
      // Clear service worker cache
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }

      // Clear browser cache
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }

      // Clear localStorage version (but keep auth tokens)
      const authToken = localStorage.getItem('jwt_token');
      const user = localStorage.getItem('user');
      const theme = localStorage.getItem('theme');

      localStorage.clear();

      // Restore auth data
      if (authToken) localStorage.setItem('jwt_token', authToken);
      if (user) localStorage.setItem('user', user);
      if (theme) localStorage.setItem('theme', theme);

      // Hard reload
      window.location.reload(true);
    } catch (error) {
      console.error('Error clearing cache:', error);
      // Fallback to simple reload
      window.location.reload(true);
    }
  }

  /**
   * Check if the user should be prompted to reload due to a new version
   * This can be called periodically or on visibility change
   */
  static async checkForUpdates(onUpdateAvailable) {
    try {
      // You can implement a version check API endpoint here
      // For now, we'll just check if chunks are accessible
      const currentVersion = this.getCurrentVersion();
      const storedVersion = this.getStoredVersion();

      if (storedVersion && storedVersion !== currentVersion && onUpdateAvailable) {
        onUpdateAvailable(currentVersion);
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  }
}

export default CacheBuster;

